import React, { useEffect, useState, useMemo } from 'react';
import { Table, Button, Modal, Form, Input, Select, DatePicker, Space, message, InputNumber, Divider, List, Avatar, Tag, Upload, Popover, Checkbox, Popconfirm, Collapse, Card } from 'antd';
import { PlusOutlined, UserOutlined, LinkOutlined, PaperClipOutlined, SettingOutlined, FilePdfOutlined, CalendarOutlined, FileSearchOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { useFileViewer } from '../store/FileViewerContext';
import LinkifyText from '../components/LinkifyText';
import TaskChecklist from '../components/TaskChecklist';
import TaskBPM from '../components/TaskBPM';
import SmartMentionsInput from '../components/SmartMentionsInput';
import dayjs from 'dayjs';
import { TasksService } from '../api/services/tasks.service';
import { ProjectsService } from '../api/services/projects.service';
import { CalendarService } from '../api/services/calendar.service';
import { UsersService } from '../api/services/users.service';
import api from '../api/client';
import GanttChartWrapper from '../components/GanttChartWrapper';
import FilePickerModal from '../components/FilePickerModal';
import UniversalFileUploadModal from '../components/UniversalFileUploadModal';
import TaskDetailModal from '../components/TaskDetailModal';
import TaskCreateModal from '../components/TaskCreateModal';

const statusOptions = [
  { value: 'new', label: 'Новая' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'review', label: 'На проверке' },
  { value: 'done', label: 'Готово' },
  { value: 'cancelled', label: 'Отменена' },
];

const priorityOptions = [
  { value: 'low', label: 'Низкий' },
  { value: 'medium', label: 'Средний' },
  { value: 'high', label: 'Высокий' },
  { value: 'critical', label: 'Критический' },
];

const taskTypeOptions = [
  { value: 'land_legal', label: 'Земельно-правовые' },
  { value: 'permissive', label: 'Разрешительные' },
  { value: 'expert', label: 'Экспертные' },
  { value: 'design_engineering', label: 'Проектно-инженерные' },
  { value: 'financial_estimate', label: 'Финансово-сметные' },
  { value: 'control_supervision', label: 'Контрольно-надзорные' },
  { value: 'logistics', label: 'Логистические' },
  { value: 'coordination', label: 'Координационные' },
  { value: 'sales_marketing', label: 'Продажно-маркетинговые' },
  { value: 'cadastral_registration', label: 'Кадастрово-регистрационные' },
  { value: 'warranty', label: 'Гарантийные (постстроительные)' },
  { value: 'judicial_claim', label: 'Судебно-претензионные' },
  { value: 'mobilization', label: 'Мобилизационные (ЧС/форс-мажор)' },
  { value: 'acceptance_transfer', label: 'Приемо-передаточные (УК)' },
  { value: 'organizational_regime', label: 'Организационно-режимные' },
  { value: 'household_administrative', label: 'Хозяйственно-административные (АХО)' },
  { value: 'gr_tasks', label: 'GR-задачи (административное лоббирование)' },
  { value: 'commercial_negotiations', label: 'Коммерческие переговоры с партнёрами' },
];


const dependencyTypeOptions = [
  { value: 'finish_to_start', label: 'Завершение → Начало (FS)' },
  { value: 'start_to_start', label: 'Начало → Начало (SS)' },
  { value: 'finish_to_finish', label: 'Завершение → Завершение (FF)' },
  { value: 'start_to_finish', label: 'Начало → Завершение (SF)' },
];


const participantRoleOptions = [
  { value: 'watcher', label: 'Наблюдатель' },
  { value: 'validator', label: 'Валидатор' },
  { value: 'approver', label: 'Утверждающий' },
  { value: 'curator', label: 'Куратор' },
  { value: 'assignee', label: 'Исполнитель' },
];

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { openFileCard } = useFileViewer();
  const extractFileId = (url) => {
    const match = url?.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
    return match ? match[1] : null;
  };
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [editProjectModalOpen, setEditProjectModalOpen] = useState(false);
  const [projectDetailModalOpen, setProjectDetailModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectCommentText, setProjectCommentText] = useState('');
  const [projectCommentFiles, setProjectCommentFiles] = useState([]);
  
  // Task Modals
  const [taskDetailModalOpen, setTaskDetailModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [participantsModalOpen, setParticipantsModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [parentTask, setParentTask] = useState(null);
  const [creating, setCreating] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('projects_table_columns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing projects_table_columns from localStorage', e);
      }
    }
    return ['name', 'budget', 'start_date', 'end_date', 'participants'];
  });
  const [visibleTaskColumns, setVisibleTaskColumns] = useState(() => {
    const saved = localStorage.getItem('projects_tasks_table_columns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing projects_tasks_table_columns from localStorage', e);
      }
    }
    return ['title', 'task_type', 'status', 'priority', 'progress', 'due_date', 'updated_at'];
  });
  const [showCompletedTasks, setShowCompletedTasks] = useState(() => {
    const saved = localStorage.getItem('projects_show_completed_tasks');
    return saved === 'true';
  });

  const [projectForm] = Form.useForm();
  const [editProjectForm] = Form.useForm();
  const [participantsForm] = Form.useForm();
  const [form] = Form.useForm();

  const handleOpenCreateSubtask = () => {
    setParentTask(selectedTask);
    setTaskDetailModalOpen(false);
    form.resetFields();
    form.setFieldsValue({
      project_id: selectedTask.project_id,
      task_type: selectedTask.task_type,
      initiator_id: user ? user.id : undefined
    });
    setModalOpen(true);
  };

  const handleCreateTask = async (values) => {
    if (!user) {
      message.error('Требуется авторизация');
      navigate('/login');
      return;
    }
    if (creating) return;
    
    setCreating(true);
    
    const payload = {
      ...values,
      start_date: values.start_date?.format('YYYY-MM-DD'),
      due_date: values.due_date?.format('YYYY-MM-DD'),
      crash_date: values.crash_date?.format('YYYY-MM-DD'),
      initiator_id: values.initiator_id || user.id,
      curator_id: values.curator_id,
      parent_id: parentTask ? parentTask.id : undefined,
    };
    
    try {
      await TasksService.createTask(payload);
      message.success('Задача создана');
      setModalOpen(false);
      setParentTask(null);
      form.resetFields();
      fetchTasks();
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        message.error('Требуется авторизация');
        navigate('/login');
      } else {
        message.error('Ошибка создания задачи: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setCreating(false);
    }
  };


  const fetchProjects = () => {
    setLoading(true);
    ProjectsService.getProjects().then(r => setProjects(r.data)).finally(() => setLoading(false));
  };

  const fetchUsers = () => {
    // v2.5.1: имена участников теперь едут с проектами (full_name) — этот список
    // лишь fallback; ошибка не должна молчать (раньше: пустой список → «ID: N» везде)
    UsersService.getUsers()
      .then(r => setUsers(r.data))
      .catch(err => console.error('Не удалось загрузить список пользователей (имена возьмутся из данных):', err));
  };

  const fetchTasks = () => {
    TasksService.getTasks().then(r => setTasks(r.data));
  };

  const avatarUrl = (avatarId) => avatarId ? `/api/v1/files/${avatarId}/content` : null;

  useEffect(() => {
    localStorage.setItem('projects_table_columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    localStorage.setItem('projects_tasks_table_columns', JSON.stringify(visibleTaskColumns));
  }, [visibleTaskColumns]);

  useEffect(() => {
    localStorage.setItem('projects_show_completed_tasks', String(showCompletedTasks));
  }, [showCompletedTasks]);

  useEffect(() => { 
    if (!user && !authLoading) {
      navigate('/login');
      return;
    }
    if (user) {
      fetchProjects();
      fetchUsers();
      fetchTasks();
    }
  }, [user, authLoading]);

  // Handle URL query parameter to open specific project
  useEffect(() => {
    if (!loading && projects.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const projectId = urlParams.get('projectId');
      if (projectId) {
        const project = projects.find(p => p.id === parseInt(projectId));
        if (project && !projectDetailModalOpen) {
          handleViewProjectDetails(project);
        }
      }
    }
  }, [loading, projects]);

  const handleCreateProject = async (values) => {
    if (!user) {
      message.error('Требуется авторизация');
      navigate('/login');
      return;
    }
    
    const curators = values.curators || [];
    const managers = values.managers || [];
    const participantsList = values.participants || [];
    
    const formattedParticipants = [
      ...managers.map(id => ({ user_id: id, role: 'manager' })),
      ...curators.map(id => ({ user_id: id, role: 'curator' })),
      ...participantsList.map(id => ({ user_id: id, role: 'participant' }))
    ];

    const payload = {
      ...values,
      start_date: values.start_date?.format('YYYY-MM-DD'),
      end_date: values.end_date?.format('YYYY-MM-DD'),
      participants: formattedParticipants,
    };
    delete payload.curators;
    delete payload.managers;
    
    try {
      await ProjectsService.createProject(payload);
      message.success('Проект создан');
      setProjectModalOpen(false);
      projectForm.resetFields();
      fetchProjects();
    } catch (error) {
      message.error('Ошибка создания проекта: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditProject = async (values) => {
    if (!selectedProject) return;
    
    const curators = values.curators || [];
    const managers = values.managers || [];
    const participantsList = values.participants || [];
    
    const formattedParticipants = [
      ...managers.map(id => ({ user_id: id, role: 'manager' })),
      ...curators.map(id => ({ user_id: id, role: 'curator' })),
      ...participantsList.map(id => ({ user_id: id, role: 'participant' }))
    ];

    const payload = {
      end_date: values.end_date?.format('YYYY-MM-DD') || null,
      participants: formattedParticipants,
      comment: values.comment
    };
    
    try {
      await ProjectsService.updateProject(selectedProject.id, payload);
      message.success('Параметры проекта обновлены');
      setEditProjectModalOpen(false);
      editProjectForm.resetFields();
      fetchProjects();
      const response = await ProjectsService.getProjectById(selectedProject.id);
      setSelectedProject(response.data);
    } catch (error) {
      message.error('Ошибка редактирования проекта: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleViewProjectDetails = async (project) => {
    try {
      const response = await ProjectsService.getProjectById(project.id);
      setSelectedProject(response.data);
    } catch (error) {
      console.warn('Failed to fetch full project details, using list data');
      setSelectedProject(project);
    }
    setProjectDetailModalOpen(true);
  };

  const handleAddProjectComment = async () => {
    if (!projectCommentText.trim() && projectCommentFiles.length === 0) return;
    
    const textToSubmit = projectCommentText;
    const filesToSubmit = projectCommentFiles;
    
    setProjectCommentText('');
    setProjectCommentFiles([]);
    
    try {
      const formData = new FormData();
      formData.append('content', textToSubmit || (filesToSubmit.length > 0 ? 'Прикреплен файл' : ''));
      if (filesToSubmit.length > 0) {
        filesToSubmit.forEach(f => formData.append('file', f));
      }

      await ProjectsService.addCommentWithFile(selectedProject.id, formData);
      message.success('Комментарий добавлен');
      
      const response = await ProjectsService.getProjectById(selectedProject.id);
      setSelectedProject(response.data);
    } catch (error) {
      setProjectCommentText(textToSubmit);
      setProjectCommentFiles(filesToSubmit);
      message.error('Ошибка добавления комментария к проекту');
    }
  };

  const handleViewTaskDetails = async (task) => {
    try {
      const response = await TasksService.getTaskById(task.id);
      setSelectedTask(response.data);
      setTaskDetailModalOpen(true);
    } catch (error) {
      message.error('Не удалось загрузить детали задачи');
      setSelectedTask(task);
      setTaskDetailModalOpen(true);
    }
  };

  const handleAddParticipant = async (values) => {
    if (!selectedTask) return;
    await TasksService.addParticipants(selectedTask.id, values);
    message.success('Участник добавлен');
    setParticipantsModalOpen(false);
    participantsForm.resetFields();
    fetchTasks();
  };

  const projectColumns = [
    { 
      title: 'Название', 
      dataIndex: 'name', 
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      render: (text, record) => (
        <Button type="link" onClick={() => handleViewProjectDetails(record)}>
          {text}
        </Button>
      )
    },
    { 
      title: 'Бюджет', 
      dataIndex: 'budget', 
      key: 'budget', 
      sorter: (a, b) => (a.budget || 0) - (b.budget || 0),
      render: (v) => v ? `${v} ₽` : '-' 
    },
    { 
      title: 'Дата начала', 
      dataIndex: 'start_date', 
      key: 'start_date', 
      sorter: (a, b) => new Date(a.start_date || 0) - new Date(b.start_date || 0),
      render: (date) => date ? dayjs(date).format('DD.MM.YYYY') : '-' 
    },
    { 
      title: 'Дата окончания', 
      dataIndex: 'end_date', 
      key: 'end_date', 
      sorter: (a, b) => new Date(a.end_date || 0) - new Date(b.end_date || 0),
      render: (date) => date ? dayjs(date).format('DD.MM.YYYY') : '-' 
    },
    {
      title: 'Участники',
      key: 'participants',
      render: (_, record) => {
        const managers = record.participants?.filter(p => p.role === 'manager').length || 0;
        const curators = record.participants?.filter(p => p.role === 'curator').length || 0;
        const participants = record.participants?.filter(p => p.role === 'participant').length || 0;
        const total = managers + curators + participants;
        return total > 0 ? total : 'Только владелец';
      }
    }
  ];

  const taskColumns = [
    { 
      title: 'Название', 
      dataIndex: 'title', 
      key: 'title',
      sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => handleViewTaskDetails(record)}
          style={{ 
            textDecoration: (record.status === 'done' || record.status === 'cancelled') ? 'line-through' : 'none',
            color: (record.status === 'done' || record.status === 'cancelled') ? 'gray' : undefined 
          }}
        >
          {text}
        </Button>
      )
    },
    {
      title: 'Тип', 
      dataIndex: 'task_type', 
      key: 'task_type',
      sorter: (a, b) => {
        const aLabel = taskTypeOptions.find(opt => opt.value === a.task_type)?.label || a.task_type || '';
        const bLabel = taskTypeOptions.find(opt => opt.value === b.task_type)?.label || b.task_type || '';
        return aLabel.localeCompare(bLabel);
      },
      render: (type) => {
        const option = taskTypeOptions.find(opt => opt.value === type);
        return option ? option.label : type;
      }
    },
    {
      title: 'Статус', 
      dataIndex: 'status', 
      key: 'status',
      sorter: (a, b) => {
        const aLabel = statusOptions.find(opt => opt.value === a.status)?.label || a.status || '';
        const bLabel = statusOptions.find(opt => opt.value === b.status)?.label || b.status || '';
        return aLabel.localeCompare(bLabel);
      },
      render: (s) => (
        <Select size="small" value={s} options={statusOptions} disabled style={{ width: 130 }} />
      ),
    },
    {
      title: 'Приоритет', 
      dataIndex: 'priority', 
      key: 'priority',
      sorter: (a, b) => {
        const aLabel = priorityOptions.find(opt => opt.value === a.priority)?.label || a.priority || '';
        const bLabel = priorityOptions.find(opt => opt.value === b.priority)?.label || b.priority || '';
        return aLabel.localeCompare(bLabel);
      },
      render: (p) => {
        const colors = { low: 'blue', medium: 'orange', high: 'red', critical: 'magenta' };
        return <Tag color={colors[p]}>{priorityOptions.find(opt => opt.value === p)?.label || p}</Tag>;
      },
    },
    { 
      title: 'Прогресс', 
      dataIndex: 'progress', 
      key: 'progress', 
      sorter: (a, b) => (a.progress || 0) - (b.progress || 0),
      render: (v) => `${v}%` 
    },
    { 
      title: 'Срок', 
      dataIndex: 'due_date', 
      key: 'due_date',
      sorter: (a, b) => new Date(a.due_date || 0) - new Date(b.due_date || 0),
      render: (date) => date ? dayjs(date).format('DD.MM.YYYY') : '-'
    },
    {
      title: 'Активность',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: (a, b) => new Date(a.updated_at || 0) - new Date(b.updated_at || 0),
      render: (date) => {
        if (!date) return '-';
        const d = dayjs(date);
        return d.isSame(dayjs(), 'day') ? d.format('HH:mm') : d.format('DD.MM.YYYY HH:mm');
      }
    }
  ];

  const filteredTasks = showCompletedTasks ? tasks : tasks.filter(t => t.status !== 'done');
  const filteredTaskColumns = taskColumns.filter(col => visibleTaskColumns.includes(col.key));

  const projectTasksTree = useMemo(() => {
    if (!selectedProject) return [];
    
    const taskMap = {};
    const roots = [];

    // First pass: map all tasks and initialize children array
    filteredTasks.forEach(task => {
      taskMap[task.id] = { ...task, children: [] };
    });

    // Second pass: attach children to parents
    filteredTasks.forEach(task => {
      if (task.parent_id && taskMap[task.parent_id]) {
        taskMap[task.parent_id].children.push(taskMap[task.id]);
      } else {
        roots.push(taskMap[task.id]);
      }
    });

    // Clean up empty children arrays so AntD doesn't show expand icon for leaves
    const cleanEmptyChildren = (nodes) => {
      nodes.forEach(node => {
        if (node.children.length === 0) {
          delete node.children;
        } else {
          cleanEmptyChildren(node.children);
        }
      });
    };
    
    cleanEmptyChildren(roots);
    
    // Фильтруем только корневые задачи, которые принадлежат этому проекту. 
    // Их подзадачи уже будут вложены в них благодаря дереву.
    return roots.filter(t => t.project_id === selectedProject.id);
  }, [filteredTasks, selectedProject]);

  const filteredProjects = projects;
  const filteredProjectColumns = projectColumns.filter(col => visibleColumns.includes(col.key));

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Проекты</h1>
        <p className="page-subtitle">Управление проектами</p>
      </div>
      
      <Space style={{ marginBottom: 16 }}>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={() => setProjectModalOpen(true)}
          style={{ 
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            border: 'none',
            boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
          }}
        >
          Новый проект
        </Button>
          <Popover
            content={
              <Checkbox.Group
                style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                value={visibleColumns}
                onChange={setVisibleColumns}
              >
                {projectColumns.map(col => (
                  <Checkbox key={col.key} value={col.key}>
                    {col.title}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            }
            title="Выберите колонки"
            trigger="click"
            placement="bottomLeft"
          >
            <Button icon={<SettingOutlined />}>Колонки</Button>
          </Popover>
        </Space>
        <style>{`
          .super-compact-table .ant-table-tbody > tr > td {
            padding: 2px 4px !important;
            font-size: 13px;
          }
          .super-compact-table .ant-table-thead > tr > th {
            padding: 4px 4px !important;
            font-size: 13px;
          }
          .super-compact-table .ant-select-selector {
            height: 24px !important;
            padding: 0 4px !important;
          }
          .super-compact-table .ant-select-selection-item {
            line-height: 22px !important;
          }
          .super-compact-table .ant-btn-sm {
            height: 22px;
            padding: 0 4px;
            font-size: 12px;
          }
          .super-compact-table .ant-tag {
            line-height: 18px;
            margin-right: 4px;
            padding: 0 4px;
          }
        `}</style>
        <Card style={{ borderRadius: 16, marginBottom: 16 }} styles={{ body: { padding: 0 } }}>
          <Table 
            dataSource={filteredProjects} 
            columns={filteredProjectColumns} 
            rowKey="id" 
            loading={loading} 
            scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }} 
            size="small"
            className="super-compact-table"
            pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200', '500'] }}
          />
        </Card>

      {/* Модальное окно создания проекта */}
      <Modal 
        title="Новый проект" 
        open={projectModalOpen} 
        onCancel={() => setProjectModalOpen(false)} 
        onOk={() => projectForm.submit()} 
        okText="Создать"
        width={600}
      >
        <Form form={projectForm} layout="vertical" onFinish={handleCreateProject}>
          <Form.Item name="name" label="Название" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <SmartMentionsInput rows={3} />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="start_date" label="Дата начала">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="end_date" label="Дата окончания">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="budget" label="Бюджет">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="managers" label="Руководители проекта">
              <Select 
                mode="multiple"
                showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
                placeholder="Выберите руководителей"
              />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="curators" label="Кураторы проекта">
              <Select 
                mode="multiple"
                showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
                placeholder="Выберите кураторов"
              />
            </Form.Item>
            <Form.Item name="participants" label="Сотрудники (участники)">
              <Select 
                mode="multiple"
                showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
                placeholder="Выберите сотрудников"
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* Модальное окно редактирования проекта */}
      <Modal 
        title="Редактировать параметры проекта" 
        open={editProjectModalOpen} 
        onCancel={() => setEditProjectModalOpen(false)} 
        onOk={() => editProjectForm.submit()} 
        okText="Сохранить"
        width={600}
      >
        <Form form={editProjectForm} layout="vertical" onFinish={handleEditProject}>
          <Form.Item name="end_date" label="Дата окончания">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="managers" label="Руководители">
              <Select 
                mode="multiple"
                showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
                placeholder="Выберите руководителей"
              />
            </Form.Item>
            <Form.Item name="curators" label="Кураторы">
              <Select 
                mode="multiple"
                showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
                placeholder="Выберите кураторов"
              />
            </Form.Item>
          </div>
          
          <Form.Item name="participants" label="Сотрудники (участники)">
            <Select 
              mode="multiple"
              showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
              placeholder="Выберите сотрудников"
            />
          </Form.Item>
          
          <Form.Item 
            name="comment" 
            label="Комментарий к изменению (обязательно)" 
            rules={[{ required: true, message: 'Пожалуйста, укажите причину изменения' }]}
          >
            <SmartMentionsInput rows={3} placeholder="Опишите причину изменения состава участников или сроков..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно деталей проекта */}
      <Modal 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{`Проект: ${selectedProject?.name || 'Загрузка...'}`}</span>
            {selectedProject && (
              <Button 
                type="text" 
                icon={<LinkOutlined />} 
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/projects?projectId=${selectedProject.id}`);
                  message.success('Ссылка на проект скопирована');
                }}
              />
            )}
          </div>
        }
        open={projectDetailModalOpen}
        onCancel={() => setProjectDetailModalOpen(false)}
        footer={[
          selectedProject && (user?.role_level >= 5 || user?.username === 'khaminov') && (
            <Popconfirm
              key="delete"
              title="Вы уверены, что хотите удалить этот проект?"
              onConfirm={async () => {
                try {
                  await ProjectsService.deleteProject(selectedProject.id);
                  message.success('Проект успешно удален');
                  setProjectDetailModalOpen(false);
                  fetchProjects();
                } catch (error) {
                  message.error('Ошибка удаления проекта');
                }
              }}
              okText="Удалить"
              cancelText="Отмена"
            >
              <Button danger>Удалить проект</Button>
            </Popconfirm>
          ),
          selectedProject && (selectedProject.owner_id === user?.id || selectedProject.participants?.some(p => p.user_id === user?.id && (p.role === 'manager' || p.role === 'curator')) || user?.role_level >= 4) && (
            <Button
              key="edit"
              onClick={() => {
                editProjectForm.setFieldsValue({
                  end_date: selectedProject.end_date ? dayjs(selectedProject.end_date) : null,
                  managers: selectedProject.participants?.filter(p => p.role === 'manager').map(p => p.user_id) || [],
                  curators: selectedProject.participants?.filter(p => p.role === 'curator').map(p => p.user_id) || [],
                  participants: selectedProject.participants?.filter(p => p.role === 'participant').map(p => p.user_id) || [],
                  comment: '',
                });
                setEditProjectModalOpen(true);
              }}
            >
              Редактировать
            </Button>
          ),
          <Button 
            key="open" 
            type="primary" 
            onClick={() => {
              setProjectDetailModalOpen(false);
              navigate(`/projects/${selectedProject.id}`);
            }}
          >
            Открыть проект
          </Button>,
          <Button key="close" onClick={() => setProjectDetailModalOpen(false)}>
            Закрыть
          </Button>,
        ].filter(Boolean)}
        width={800}
        style={{ top: 20 }}
      >
        {!selectedProject ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Загрузка данных проекта...</p>
          </div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <div>
                <h4>Основная информация</h4>
                <p><strong>Описание:</strong> {selectedProject.description ? <LinkifyText text={selectedProject.description} style={{ display: 'inline' }} /> : 'Нет описания'}</p>
                <p><strong>Бюджет:</strong> {selectedProject.budget ? `${selectedProject.budget} ₽` : 'Не указан'}</p>
                <p><strong>Руководители:</strong> {selectedProject.participants?.filter(p => p.role === 'manager').map(p => p.full_name || users.find(u => String(u.id) === String(p.user_id))?.full_name || `ID: ${p.user_id}`).join(', ') || 'Нет'}</p>
                <p><strong>Кураторы:</strong> {selectedProject.participants?.filter(p => p.role === 'curator').map(p => p.full_name || users.find(u => String(u.id) === String(p.user_id))?.full_name || `ID: ${p.user_id}`).join(', ') || 'Только владелец'}</p>
                <p><strong>Сотрудники:</strong> {selectedProject.participants?.filter(p => p.role === 'participant').map(p => p.full_name || users.find(u => String(u.id) === String(p.user_id))?.full_name || `ID: ${p.user_id}`).join(', ') || 'Нет'}</p>
              </div>
              <div>
                <h4>Даты</h4>
                <p><strong>Дата начала:</strong> {selectedProject.start_date ? dayjs(selectedProject.start_date).format('DD.MM.YYYY') : 'Не указана'}</p>
                <p><strong>Дата окончания:</strong> {selectedProject.end_date ? dayjs(selectedProject.end_date).format('DD.MM.YYYY') : 'Не указана'}</p>
                <p><strong>Создан:</strong> {selectedProject.created_at ? dayjs(selectedProject.created_at).format('DD.MM.YYYY HH:mm') : 'Не указано'}</p>
              </div>
            </div>

            <Divider />
            
            <Collapse
              items={[
                {
                  key: 'bpm',
                  label: <b>Маршруты согласования</b>,
                  children: (
                    <TaskBPM project={selectedProject} onUpdate={fetchProjects} hideStartButton />
                  )
                }
              ]}
              style={{ marginBottom: 24 }}
            />
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h4>Задачи проекта</h4>
              <Space>
                <Checkbox 
                  checked={showCompletedTasks} 
                  onChange={(e) => setShowCompletedTasks(e.target.checked)}
                >
                  Показывать завершенные
                </Checkbox>
                <Popover
                  content={
                    <Checkbox.Group
                    style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                    value={visibleTaskColumns}
                    onChange={setVisibleTaskColumns}
                  >
                    {taskColumns.map(col => (
                      <Checkbox key={col.key} value={col.key}>
                        {col.title}
                      </Checkbox>
                    ))}
                  </Checkbox.Group>
                }
                title="Выберите колонки"
                trigger="click"
                placement="bottomLeft"
              >
                <Button icon={<SettingOutlined />}>Колонки задач</Button>
              </Popover>
              </Space>
            </div>
            <Table 
              dataSource={projectTasksTree} 
              columns={filteredTaskColumns} 
              rowKey="id" 
              pagination={{ pageSize: 5 }} 
              size="small"
              scroll={{ x: 'max-content' }}
            />

            <Divider />
            
            <div style={{ marginTop: '24px' }}>
              <h4>Комментарии к проекту</h4>
              <List
                itemLayout="horizontal"
                dataSource={selectedProject.comments || []}
                locale={{ emptyText: 'Нет комментариев' }}
                renderItem={(comment) => {
                  const author = users.find(u => String(u.id) === String(comment.user_id));
                  return (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar icon={<UserOutlined />} />}
                        title={comment.author_full_name || (author ? author.full_name || author.email : null) || `ID: ${comment.user_id}`}
                        description={
                          <>
                            <LinkifyText text={comment.content} />
                            {comment.attached_files && comment.attached_files.length > 0 ? (
                              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {comment.attached_files.map((f, i) => (
                                  <a key={i} onClick={() => {
                                    const id = f.file_id || extractFileId(f.file_url);
                                    if (id) {
                                      openFileCard(id, { project_id: selectedProject.id });
                                    } else {
                                      window.open(f.file_url, '_blank');
                                    }
                                  }} style={{ 
                                    cursor: 'pointer', 
                                    color: '#1890ff',
                                    border: comment.file_approval_status === 'active' ? '2px solid #faad14' : comment.file_approval_status === 'cancelled' ? '2px solid #f5222d' : comment.file_approval_status === 'completed' ? '2px solid #52c41a' : 'none',
                                    backgroundColor: comment.file_approval_status === 'active' ? 'rgba(250, 173, 20, 0.05)' : comment.file_approval_status === 'completed' ? 'rgba(82, 196, 26, 0.05)' : comment.file_approval_status === 'cancelled' ? 'rgba(245, 34, 45, 0.05)' : 'transparent',
                                    padding: comment.file_approval_status ? '2px 4px' : '0',
                                    borderRadius: comment.file_approval_status ? '4px' : '0',
                                    display: 'inline-block'
                                  }}>
                                    <PaperClipOutlined /> {f.file_name || 'Прикрепленный файл'}
                                  </a>
                                ))}
                              </div>
                            ) : comment.file_url ? (
                              <div style={{ marginTop: 8 }}>
                                <a onClick={() => {
                                  const id = extractFileId(comment.file_url);
                                  if (id) {
                                    openFileCard(id, { project_id: selectedProject.id });
                                  } else {
                                    window.open(comment.file_url, '_blank');
                                  }
                                }} style={{ 
                                  cursor: 'pointer', 
                                  color: '#1890ff',
                                  border: comment.file_approval_status === 'active' ? '2px solid #faad14' : comment.file_approval_status === 'cancelled' ? '2px solid #f5222d' : comment.file_approval_status === 'completed' ? '2px solid #52c41a' : 'none',
                                  backgroundColor: comment.file_approval_status === 'active' ? 'rgba(250, 173, 20, 0.05)' : comment.file_approval_status === 'completed' ? 'rgba(82, 196, 26, 0.05)' : comment.file_approval_status === 'cancelled' ? 'rgba(245, 34, 45, 0.05)' : 'transparent',
                                  padding: comment.file_approval_status ? '2px 4px' : '0',
                                  borderRadius: comment.file_approval_status ? '4px' : '0',
                                  display: 'inline-block'
                                }}>
                                  <PaperClipOutlined /> {comment.file_name || 'Прикрепленный файл'}
                                </a>
                              </div>
                            ) : null}
                            <span style={{ fontSize: '0.8em', color: 'gray' }}>
                              {dayjs(comment.created_at).format('DD.MM.YYYY HH:mm')}
                            </span>
                          </>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
              
              <div style={{ marginTop: 16 }}>
                <SmartMentionsInput 
                  rows={3} 
                  value={projectCommentText} 
                  onChange={(val) => setProjectCommentText(val)} 
                  placeholder="Добавить комментарий к проекту..."
                  onSubmit={handleAddProjectComment}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <Upload 
                    beforeUpload={(file) => { setProjectCommentFiles(prev => [...prev, file]); return false; }} 
                    onRemove={(file) => setProjectCommentFiles(prev => prev.filter(f => f.uid !== file.uid))}
                    multiple={true}
                    fileList={projectCommentFiles}
                  >
                    <Button icon={<PaperClipOutlined />}>Прикрепить файлы</Button>
                  </Upload>
                  <Button type="primary" onClick={handleAddProjectComment}>
                    Отправить
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      
      <TaskCreateModal
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setParentTask(null);
          form.resetFields();
        }}
        form={form}
        onFinish={handleCreateTask}
        users={users}
        projects={projects}
        tasks={tasks}
        parentTask={parentTask}
        taskTypeOptions={taskTypeOptions}
        priorityOptions={priorityOptions}
        user={user}
        confirmLoading={creating}
      />
      
      <TaskDetailModal 
        open={taskDetailModalOpen}
        onClose={() => setTaskDetailModalOpen(false)}
        selectedTask={selectedTask}
        setSelectedTask={setSelectedTask}
        users={users}
        projects={projects}
        tasks={tasks}
        onUpdate={fetchTasks}
        onOpenCreateSubtask={handleOpenCreateSubtask}
      />
</div>
  );
}
